import { Grid } from "@chakra-ui/react";
import { useIntervalEffect, useMeasure, useRerender } from "@react-hookz/web";
import { PanInfo } from "framer-motion";
import { range } from "lodash";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { usePrevious, useSet } from "react-use";
import { Task } from "../../../hooks/api/karl-farm/constants";
import { Score } from "../../../hooks/api/karl-farm/useScores";
import { getScoreColor } from "../../../utils/getScoreColor";
import { getSkewedScore } from "../../../utils/getSkewedScore";
import { scheduleTasks } from "../../../utils/scheduleTasks";
import { transitionWithoutBounce } from "../../../utils/transitions";
import { useSafeSetTimeout } from "../../../utils/useSafeTimeout";
import { CalendarItem } from "./CalendarItem";
import { GridContainer } from "./Grid";
import { Headers } from "./Headers";
import { TimeIndicator } from "./TimeIndicator";
import { TimeSlotColumns } from "./TimeSlotColumns";
import { TimestampColumn } from "./TimestampColumn";

export type CalendarTask = {
  id: string;
  title: string;
  location: string;
  start: Date;
  end: Date;
  duration: number;
  score: Score | null;
  userIds: string[];
  task: Task;
};

const calculateDragOffset = (
  info: PanInfo,
  cellWidth: number,
  cellHeight: number
): { days: number; halfHours: number } => {
  const days = Math.round(info.offset.x / cellWidth);
  const halfHours = Math.round(info.offset.y / (cellHeight / 2));
  return { days, halfHours };
};

const calculateNewDatesFromOffset = (
  start: Date,
  duration: number,
  offset: { days: number; halfHours: number },
  bounds: {
    minDate: Date;
    maxDate: Date;
  }
): {
  start: Date;
  end: Date;
} => {
  const startWithDaysOffset = new Date(start);
  startWithDaysOffset.setDate(startWithDaysOffset.getDate() + offset.days);
  const boundedStartDateMillis = Math.max(
    bounds.minDate.getTime(),
    Math.min(startWithDaysOffset.getTime(), bounds.maxDate.getTime())
  );

  const newMinutes =
    start.getHours() * 60 + start.getMinutes() + offset.halfHours * 30;
  const minMinutes = 0; // 00:00
  const maxMinutes = 60 * 24 - 30; // 23:30
  const boundedNewMinutes = Math.max(
    minMinutes,
    Math.min(newMinutes, maxMinutes)
  );

  const boundedStartDate = new Date(boundedStartDateMillis);
  boundedStartDate.setHours(0, boundedNewMinutes, 0, 0);

  const end = new Date(boundedStartDate);
  end.setMinutes(end.getMinutes() + duration);

  return { start: boundedStartDate, end };
};

const nullMeasure = { width: 0, height: 0 };

type CalendarProps = {
  daysToDisplay: number;
  startDate: Date;
  endDate: Date;
  tasks: CalendarTask[];
  selectedTaskId: string | null;
  timeSlotColors: string[][];
  onEmptyTimeSlotClick: (start: Date) => void;
  onTaskClick: (taskId: string) => void;
  onTaskDrag: (taskId: string, start: Date, end: Date) => void;
  onTaskDragEnd: (taskId: string, start: Date, end: Date) => void;
};
export const Calendar: FC<CalendarProps> = ({
  daysToDisplay,
  startDate,
  endDate,
  tasks,
  selectedTaskId,
  timeSlotColors,
  onEmptyTimeSlotClick,
  onTaskClick,
  onTaskDrag,
  onTaskDragEnd,
}) => {
  const now = new Date();

  const rerender = useRerender();
  useIntervalEffect(() => {
    // Make sure component rerenders once a minute to update the current time indicator
    rerender();
  }, 1000 * 60);

  const [gridMeasures, gridRef] = useMeasure<HTMLDivElement>();
  const { width: gridWidth, height: gridHeight } = gridMeasures || nullMeasure;
  const prevGridWidth = usePrevious(gridWidth);
  const didGridWidthChange = prevGridWidth !== gridWidth;

  const [timestampHeaderMeasures, timestampHeaderRef] =
    useMeasure<HTMLDivElement>();
  const { width: timestampHeaderWidth, height: timestampHeaderHeightNoBorder } =
    timestampHeaderMeasures || nullMeasure;
  const timestampHeaderHeight = timestampHeaderHeightNoBorder + 1;

  const allColumnMeasures = [
    useMeasure<HTMLDivElement>(),
    useMeasure<HTMLDivElement>(),
    useMeasure<HTMLDivElement>(),
    useMeasure<HTMLDivElement>(),
    useMeasure<HTMLDivElement>(),
    useMeasure<HTMLDivElement>(),
    useMeasure<HTMLDivElement>(),
    useMeasure<HTMLDivElement>(),
    useMeasure<HTMLDivElement>(),
    useMeasure<HTMLDivElement>(),
    useMeasure<HTMLDivElement>(),
  ];
  const columnMeasures = allColumnMeasures.slice(0, daysToDisplay);
  const columnRefs = columnMeasures.map(([, ref]) => ref);
  const columnWidths = columnMeasures.map(([measures]) => measures?.width || 0);

  const setTimeout = useSafeSetTimeout();

  const dates = range(daysToDisplay).map((i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return date;
  });

  const [disabledItems, { add: disableItem, remove: enableItem }] =
    useSet<string>(new Set());
  const [draggedTask, setDraggedTask] = useState<string | null>(null);

  const [panStartDate, setPanStartDate] = useState<Date | null>(null);

  const [cellMeasures, cellRef] = useMeasure<HTMLDivElement>();
  const { width: cellWidthNoBorder, height: cellHeightNoBorder } =
    cellMeasures || nullMeasure;
  const cellWidth = cellWidthNoBorder + 1; // +1 for the border
  const cellHeight = cellHeightNoBorder + 1; // +1 for the border

  const calculateTopOffsetInPercent = useCallback(
    (date: Date): `${string}%` => {
      const offsetInPixels =
        timestampHeaderHeight +
        (date.getHours() + date.getMinutes() / 60) * cellHeight;
      const ratio = offsetInPixels / gridHeight;
      return `${ratio * 100}%`;
    },
    [gridHeight, timestampHeaderHeight, cellHeight]
  );

  const calculateLeftOffsetInPercent = useCallback(
    (date: Date): `${string}%` => {
      const days = Math.floor(
        (date.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      const offsetInPixels =
        timestampHeaderWidth +
        columnWidths.slice(0, days).reduce((a, b) => a + b, 0);
      const ratio = offsetInPixels / gridWidth;
      return `${ratio * 100}%`;
    },
    [gridWidth, timestampHeaderWidth, columnWidths, startDate]
  );

  const handleEmptyTimeSlotClick = (column: number, hour: number) => {
    onEmptyTimeSlotClick(new Date(dates[column].setHours(hour, 0, 0, 0)));
  };

  const handleCalendarItemPan: (taskId: string, info: PanInfo) => void = (
    taskId,
    info
  ) => {
    if (draggedTask === null) setDraggedTask(taskId);
    if (!disabledItems.has(taskId)) disableItem(taskId);

    const task = tasks.find((task) => task.id === taskId);
    if (!task) return;

    if (!panStartDate) {
      setPanStartDate(task.start);
    }
    const safePanStartDate = panStartDate ?? task.start;

    const offset = calculateDragOffset(info, cellWidth, cellHeight);
    const { start, end } = calculateNewDatesFromOffset(
      safePanStartDate,
      task.duration,
      offset,
      {
        minDate: startDate,
        maxDate: endDate,
      }
    );

    if (
      task.start.getTime() !== start.getTime() ||
      task.end.getTime() !== end.getTime()
    ) {
      onTaskDrag(taskId, start, end);
    }
  };

  const handleCalendarItemPanEnd: (
    taskId: string,
    e: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => void = (taskId, _, info) => {
    // Wait 100ms before enabling the item again to prevent the item from being
    // clicked when the user releases the mouse button
    setTimeout(() => {
      enableItem?.(taskId);
    }, 100);
    setDraggedTask(null);

    const offset = calculateDragOffset(info, cellWidth, cellHeight);
    const task = tasks.find((task) => task.id === taskId);
    if (!task) return;

    const safePanStartDate = panStartDate ?? task.start;

    const { start, end } = calculateNewDatesFromOffset(
      safePanStartDate,
      task.duration,
      offset,
      {
        minDate: startDate,
        maxDate: endDate,
      }
    );

    onTaskDragEnd(taskId, start, end);

    setPanStartDate(null);
  };

  const sortedTasks = useMemo(() => {
    return tasks.sort((a, b) => {
      const startAscendingDiff = a.start.getTime() - b.start.getTime();
      if (startAscendingDiff !== 0) {
        return startAscendingDiff;
      }

      const endDescendingDiff = b.end.getTime() - a.end.getTime();
      if (endDescendingDiff !== 0) {
        return endDescendingDiff;
      }

      return a.id.localeCompare(b.id);
    });
  }, [tasks]);

  const taskBatches: CalendarTask[][] = useMemo(() => {
    const batches: CalendarTask[][] = [];
    let currentBatch: CalendarTask[] = [];
    let currentBatchEnd: Date | null = null;
    sortedTasks.forEach((task) => {
      if (currentBatchEnd === null || task.start >= currentBatchEnd) {
        currentBatch = [task];
        batches.push(currentBatch);
        currentBatchEnd = task.end;
      } else {
        currentBatch.push(task);
        if (task.end > currentBatchEnd) {
          currentBatchEnd = task.end;
        }
      }
    });
    return batches;
  }, [sortedTasks]);

  const taskSchedules = useMemo(() => {
    return taskBatches.map((batch) => scheduleTasks(batch));
  }, [taskBatches]);

  const tasksWithPositions = useMemo(() => {
    return taskSchedules.flatMap((schedule) => {
      return schedule.flatMap((resource, resourceI) => {
        return resource.map((task) => {
          const dayColumnLeft = calculateLeftOffsetInPercent(task.start);

          const resourceCount = schedule.length;
          const width: number = (columnWidths[0] * 0.9) / resourceCount;
          const left = `calc(${dayColumnLeft} + ${width * resourceI}px)`;
          const top: `${string}%` = calculateTopOffsetInPercent(task.start);
          const height: `${string}px` = `${
            (task.duration / 60) * cellHeight
          }px`;
          return {
            ...task,
            top,
            left,
            width,
            height,
          };
        });
      });
    });
  }, [
    calculateLeftOffsetInPercent,
    calculateTopOffsetInPercent,
    cellHeight,
    columnWidths,
    taskSchedules,
  ]);

  useEffect(() => {
    if (gridRef.current) {
      // Scroll to 6am
      gridRef.current.scrollTop = cellHeight * 5.75;
    }
  }, [gridRef, cellHeight]);

  return (
    <GridContainer>
      <Grid
        ref={gridRef}
        position="relative"
        gridTemplateColumns={`auto repeat(${daysToDisplay}, 1fr)`}
        gridTemplateRows="auto 1fr"
        overflowY="auto"
      >
        <Grid
          position="sticky"
          top="0"
          gridColumn={`1 / span ${daysToDisplay + 1}`}
          gridRow="1 / 2"
          zIndex="3"
          background="white"
          gridTemplateColumns={`auto repeat(${daysToDisplay}, 1fr)`}
          gridTemplateRows="auto"
        >
          <Headers
            timestampHeaderRef={timestampHeaderRef}
            dates={dates}
            gridWidth={gridWidth}
          />
        </Grid>
        <TimestampColumn />
        <TimeSlotColumns
          daysToDisplay={daysToDisplay}
          backgroundColors={timeSlotColors}
          columnRefs={columnRefs}
          cellRef={cellRef}
          onEmptyTimeSlotClick={handleEmptyTimeSlotClick}
        />

        {tasksWithPositions.map((task) => {
          const isDragging = task.id === draggedTask;
          const isSelected = task.id === selectedTaskId;
          const zIndex = isDragging ? 2 : 1;
          const { top, left, width, height } = task;

          const animationProps = { zIndex, top, left, width, height };

          const score = task.score?.value;
          const skewedScore = score != null ? getSkewedScore(score) : null;

          return (
            <CalendarItem
              key={task.id}
              task={task}
              conditionIconColor={
                skewedScore != null
                  ? getScoreColor(skewedScore, false)
                  : `hsl(${60}, 0%, 50%)`
              }
              disabled={disabledItems.has(task.id)}
              onClick={() => onTaskClick(task.id)}
              onPan={(_, info) => handleCalendarItemPan(task.id, info)}
              onPanEnd={(e, info) => handleCalendarItemPanEnd(task.id, e, info)}
              isHighlighted={isDragging || isSelected}
              initial={animationProps}
              animate={animationProps}
              transition={
                didGridWidthChange
                  ? {
                      type: "tween",
                      duration: 0,
                    }
                  : transitionWithoutBounce
              }
            />
          );
        })}

        {startDate.getTime() < now.getTime() &&
          now.getTime() < endDate.getTime() && (
            <TimeIndicator
              animate={{
                top: calculateTopOffsetInPercent(now),
                left: calculateLeftOffsetInPercent(now),
                width: columnWidths[0],
              }}
            />
          )}
      </Grid>
    </GridContainer>
  );
};
